import { Button } from '@fox/atoms/src/button/index';
import { MuiAlert } from '@fox/atoms/src/mui-alert/index';
import { TextField } from '@fox/atoms/src/text-field/index';
import { digitsOnly } from '@fox/atoms/src/inputs/currency-input';
import { useTempDataContext } from '@fox/atoms/src/temp-data-context/index';
import { FlexColumnSpaced, FlexRowSpacedRightAligned } from '@fox/layout/src/flex';
import { zodResolver } from '@hookform/resolvers/zod';
import { nanoid } from 'nanoid';
import React from 'react';
import { SubmitHandler, UseFormRegister, useForm } from 'react-hook-form';
import { z } from 'zod';

export const directDebitSchema = z.object({
	bsb: z.string().regex(/^\d{6}$/, { message: 'Bank account must be 6 digits.' }),
	accountNumber: z.string().regex(/^\d{6,9}$/, { message: 'Account number must be 6-9 digits.' }),
	accountName: z
		.string()
		.min(2, { message: 'Account name must be at least 2 characters' })
		.max(32, { message: 'Account name must be at most 32 characters' })
});
export type DebitType = z.infer<typeof directDebitSchema>;

export const DEBIT_FORM_ACCOUNT_NAME_ELEMENT = 'debit-name';
export const DEBIT_FORM_BSB_ELEMENT = 'debit-bsb';
export const DEBIT_FORM_ACCOUNT_NUMBER_ELEMENT = 'debit-number';

export function DebitForm({
	defaultValues,
	submitFunc,
	onCancel,
	apiErrors,
	showSave = true,
	elementIDSuffix
}: {
	submitFunc: SubmitHandler<DebitType>;
	onCancel?: () => void;
	defaultValues?: Partial<DebitType>;
	apiErrors?: string[] | null;
	showSave?: boolean;
	elementIDSuffix?: string;
}) {
	// RHF violates the rules of hooks, which the React compiler enforces. For now we are not optimising components that 
	// use RHF, as a workaround, but eventually all of these usages need to be updated to use tanstack-form.
	// See: https://github.com/react-hook-form/react-hook-form/issues/12598
	// This string ensures the React compiler does not optimise (memoize) this component
	"use no memo"
	
	const tempData = useTempDataContext();
	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting, isValid },
		watch
	} = useForm<DebitType>({
		mode: 'onBlur',
		resolver: zodResolver(directDebitSchema),
		defaultValues: {
			bsb: defaultValues?.bsb ?? tempData?.debitFormData?.bsb,
			accountNumber: defaultValues?.accountNumber ?? tempData?.debitFormData?.accountNumber,
			accountName: defaultValues?.accountName ?? tempData?.debitFormData?.accountName
		}
	});
	if (tempData) {
		tempData.debitFormDataValid = isValid;
	}
	const r: UseFormRegister<DebitType> = (name, opts) => {
		return {
			...register(name, opts),
			status: errors[name] ? 'error' : undefined,
			helperText: errors[name] ? errors[name]?.message : null
		};
	};

	React.useEffect(() => {
		if (!tempData) return;
		const subscription = watch((value, { name, type }) => {
			tempData.debitFormData = value;
			tempData.dirty = true;
		});
		return () => subscription.unsubscribe();
	}, [watch]);

	React.useImperativeHandle(
		r => {
			if (tempData) tempData.submitFunc = r;
		},
		() => ({
			submit: handleSubmit(submitFunc)
		})
	);
	const suffix = elementIDSuffix || nanoid();

	return (
		<form onSubmit={handleSubmit(submitFunc)}>
			<FlexColumnSpaced>
				<TextField size="small" label="Account Name" {...r('accountName')} id={`${DEBIT_FORM_ACCOUNT_NAME_ELEMENT}-${suffix}`} />

				<TextField size="small" label="BSB" {...r('bsb', { setValueAs: digitsOnly })} id={`${DEBIT_FORM_BSB_ELEMENT}-${suffix}`} />

				<TextField
					size="small"
					label="Account Number"
					{...r('accountNumber')}
					id={`${DEBIT_FORM_ACCOUNT_NUMBER_ELEMENT}-${suffix}`}
				/>

				{apiErrors?.length ? (
					<MuiAlert color="error" severity="error">
						{apiErrors.map(a => (
							<React.Fragment key={a}>
								{a}
								<br />
							</React.Fragment>
						))}
					</MuiAlert>
				) : null}

				<FlexRowSpacedRightAligned style={{ marginBottom: '0.5rem' }}>
					{onCancel ? (
						<Button loading={isSubmitting} color="secondary" variant="secondary" onClick={() => onCancel()}>
							Cancel
						</Button>
					) : null}
					{showSave ? (
						<Button loading={isSubmitting} type="submit" color="primary" variant="primary">
							Save
						</Button>
					) : null}
				</FlexRowSpacedRightAligned>
			</FlexColumnSpaced>
		</form>
	);
}
